import { useCallback, useEffect, useState } from 'react'
import { api, ApiError } from '@/api/client'
import type { JobLocationRow, JobLocationSuggestion } from '@/api/types'

/**
 * Deciding where each job actually is.
 *
 * Measured 25 Aug 2026: JobLocation held ZERO rows for 327 open jobs, and 86
 * of those jobs carry no street address at all — so no geocoder will ever
 * place them. What will place them is the crews who go there. Every "on site"
 * tap is a free reading, and the median of a job's readings is its location.
 *
 * NOTHING ELSE IN THE FEATURE WORKS UNTIL THIS DOES. Until a job is pinned
 * every reading against it comes back "No site pinned" — the recording is
 * real, but no verdict can be drawn from it.
 *
 * PINNING IS DELIBERATE, NEVER AUTOMATIC. Every future check-in at a job is
 * classified against its pin, so one dropped in the wrong place silently
 * converts honest readings into "Away from site". Accepting a suggestion is
 * one tap, but it is a tap made after looking at how many readings there were
 * and how far apart they sat: three readings ten metres apart is a site, three
 * readings four kilometres apart is three different places and the median
 * means nothing.
 */
export function JobLocationsPage() {
  const [suggestions, setSuggestions] = useState<JobLocationSuggestion[]>([])
  const [pinned, setPinned] = useState<JobLocationRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [flash, setFlash] = useState<string | null>(null)
  const [saving, setSaving] = useState<string | null>(null)

  /** Radius per job, before it is committed. Default 500 m. */
  const [radius, setRadius] = useState<Record<string, string>>({})

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [s, p] = await Promise.all([
        api.locationSuggestions(true),
        api.jobLocations(),
      ])
      setSuggestions(s)
      setPinned(p)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load job locations')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function accept(s: JobLocationSuggestion) {
    const r = Number(radius[s.job_no] ?? 500)
    if (!Number.isFinite(r) || r < 25 || r > 20000) {
      setError('Radius must be between 25 and 20,000 metres.')
      return
    }
    setSaving(s.job_no)
    setError(null)
    try {
      await api.setJobLocation(s.job_no, {
        latitude: s.suggested_latitude,
        longitude: s.suggested_longitude,
        radius_meters: r,
        source: 'CheckInMedian',
      })
      setFlash(
        `${s.job_no} pinned from ${s.reading_count} reading` +
          `${s.reading_count === 1 ? '' : 's'}. Every check-in there now resolves.`,
      )
      await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not pin that job')
    } finally {
      setSaving(null)
    }
  }

  async function reradius(row: JobLocationRow) {
    const r = Number(radius[row.job_no] ?? row.radius_meters)
    if (!Number.isFinite(r) || r < 25 || r > 20000) {
      setError('Radius must be between 25 and 20,000 metres.')
      return
    }
    if (r === row.radius_meters) return
    setSaving(row.job_no)
    setError(null)
    try {
      await api.setJobLocation(row.job_no, {
        latitude: row.latitude,
        longitude: row.longitude,
        radius_meters: r,
        source: row.geocode_source ?? 'Manual',
      })
      setFlash(`${row.job_no} radius set to ${r} m.`)
      await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that radius')
    } finally {
      setSaving(null)
    }
  }

  /* --- Moving or clearing a pin -------------------------------------------
   *
   * Asked for after the first real pin (Tim Brickell, 9 Oct 2026): once a job
   * was pinned there was no way to adjust it, or to take a test pin off
   * somebody's house. Two different acts, kept apart:
   *
   *   MOVE  - new coordinates, same job. Pasted straight from Google Maps
   *           ("47.414361, -122.237804" is what a right-click there copies),
   *           because that is where people will be looking at the site.
   *   CLEAR - no pin at all. The readings fall back to "No site pinned" and
   *           the job returns to the suggestion queue.
   */
  const [moving, setMoving] = useState<string | null>(null)
  const [coords, setCoords] = useState('')

  /** "lat, long" in the form Google Maps copies, or null if it is not that. */
  function parseCoords(s: string): { latitude: number; longitude: number } | null {
    const m = s.trim().match(/^(-?\d+(?:\.\d+)?)\s*[,\s]\s*(-?\d+(?:\.\d+)?)$/)
    if (!m) return null
    const latitude = Number(m[1])
    const longitude = Number(m[2])
    if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return null
    return { latitude, longitude }
  }

  async function savePosition(jobNo: string, radiusMeters: number) {
    const p = parseCoords(coords)
    if (!p) {
      setError('Paste the position as "latitude, longitude" - for example 47.414361, -122.237804.')
      return
    }
    setSaving(jobNo)
    setError(null)
    try {
      await api.setJobLocation(jobNo, {
        ...p,
        radius_meters: radiusMeters,
        source: 'Manual',
      })
      setFlash(`${jobNo} moved to ${p.latitude.toFixed(6)}, ${p.longitude.toFixed(6)}.`)
      setMoving(null)
      setCoords('')
      await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not move that pin')
    } finally {
      setSaving(null)
    }
  }

  async function clearPin(row: JobLocationRow) {
    if (!confirm(`Remove the pin for ${row.job_no}? Its readings go back to "No site pinned".`))
      return
    setSaving(row.job_no)
    setError(null)
    try {
      await api.clearJobLocation(row.job_no)
      setFlash(`${row.job_no} pin removed.`)
      await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not remove that pin')
    } finally {
      setSaving(null)
    }
  }

  /* Pinning a job nobody has checked in at yet, from a map. Without this a
     cleared pin on a job with no readings could never be put back. */
  const [newJob, setNewJob] = useState('')
  const [newCoords, setNewCoords] = useState('')

  async function pinByHand() {
    const jobNo = newJob.trim().toUpperCase()
    const p = parseCoords(newCoords)
    if (!jobNo || !p) {
      setError('Enter a job number and a position as "latitude, longitude".')
      return
    }
    setSaving(jobNo)
    setError(null)
    try {
      await api.setJobLocation(jobNo, { ...p, radius_meters: 500, source: 'Manual' })
      setFlash(`${jobNo} pinned by hand.`)
      setNewJob('')
      setNewCoords('')
      await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not pin that job')
    } finally {
      setSaving(null)
    }
  }

  /** A cluster this wide is not one place. */
  const spreadWarn = (m: number | null) => m != null && m > 300

  if (loading && suggestions.length === 0 && pinned.length === 0) {
    return <div className="card">Loading…</div>
  }

  return (
    <div>
      <h1>Job locations</h1>
      <p className="muted">
        Where each job is, worked out from the crews who went there. A job has
        to be pinned before any check-in against it can say anything —{' '}
        <strong>until then every reading reads “No site pinned”.</strong>
      </p>

      {flash && <div className="alert alert-ok">{flash}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <h2>Suggested from check-ins ({suggestions.length})</h2>
        {suggestions.length === 0 ? (
          <p className="muted">
            Nothing to pin. Suggestions appear once supervisors start tapping
            “I’m on site” — each job needs at least one reading.
          </p>
        ) : (
          <>
            <p className="muted" style={{ marginTop: 0, fontSize: '0.8125rem' }}>
              The median of each job’s readings. Check the spread before
              accepting: a wide one means people tapped in several different
              places, and the median is then halfway between them rather than
              anywhere anybody stood.
            </p>
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>Job</th>
                    <th className="num">Readings</th>
                    <th className="num">Spread</th>
                    <th>Suggested position</th>
                    <th className="num">Radius (m)</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {suggestions.map((s) => (
                    <tr key={s.job_no}>
                      <td className="mono">{s.job_no}</td>
                      <td className="num">{s.reading_count}</td>
                      <td className="num">
                        {s.max_spread_meters == null ? (
                          <span className="muted">–</span>
                        ) : spreadWarn(s.max_spread_meters) ? (
                          <span className="badge badge-warn">
                            {Math.round(s.max_spread_meters)} m
                          </span>
                        ) : (
                          `${Math.round(s.max_spread_meters)} m`
                        )}
                      </td>
                      <td className="mono" style={{ fontSize: '0.8125rem' }}>
                        {Number(s.suggested_latitude).toFixed(6)},{' '}
                        {Number(s.suggested_longitude).toFixed(6)}
                        {/* A link out rather than an embedded map: this page
                            must not depend on a third-party script to be
                            usable, and one job at a time is the actual need. */}
                        <div>
                          <a
                            href={`https://www.google.com/maps?q=${s.suggested_latitude},${s.suggested_longitude}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Look at it
                          </a>
                        </div>
                      </td>
                      <td className="num">
                        <input
                          type="number"
                          step="25"
                          min="25"
                          max="20000"
                          style={{ width: '6rem' }}
                          value={radius[s.job_no] ?? '500'}
                          onChange={(e) =>
                            setRadius((r) => ({ ...r, [s.job_no]: e.target.value }))
                          }
                          aria-label={`Radius for ${s.job_no}`}
                        />
                      </td>
                      <td>
                        <button
                          className="btn"
                          disabled={saving === s.job_no}
                          onClick={() => void accept(s)}
                        >
                          {saving === s.job_no ? 'Pinning…' : 'Pin here'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <div className="card">
        <h2>Pinned ({pinned.length})</h2>
        <p className="muted" style={{ marginTop: 0, fontSize: '0.8125rem' }}>
          To move a pin, right-click the spot in Google Maps, click the
          coordinates to copy them, and paste them in.
        </p>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: '0.75rem' }}>
          <input
            value={newJob}
            onChange={(e) => setNewJob(e.target.value)}
            placeholder="Job no."
            aria-label="Job number to pin by hand"
            style={{ width: '7rem' }}
            maxLength={7}
          />
          <input
            value={newCoords}
            onChange={(e) => setNewCoords(e.target.value)}
            placeholder="latitude, longitude"
            aria-label="Position to pin"
            style={{ minWidth: '14rem' }}
          />
          <button
            className="btn btn-secondary"
            disabled={!newJob.trim() || !newCoords.trim() || saving !== null}
            onClick={() => void pinByHand()}
          >
            Pin by hand
          </button>
        </div>
        {pinned.length === 0 ? (
          <p className="muted">No job has a location yet.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Job</th>
                  <th>Position</th>
                  <th className="num">Radius (m)</th>
                  <th className="num">Readings</th>
                  <th>Pinned by</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pinned.map((l) => (
                  <tr key={l.job_no}>
                    <td>
                      <span className="mono">{l.job_no}</span>
                      <div className="muted" style={{ fontSize: '0.75rem' }}>
                        {l.job_description}
                      </div>
                    </td>
                    <td className="mono" style={{ fontSize: '0.8125rem' }}>
                      {moving === l.job_no ? (
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          <input
                            value={coords}
                            onChange={(e) => setCoords(e.target.value)}
                            placeholder="47.414361, -122.237804"
                            aria-label={`New position for ${l.job_no}`}
                            style={{ minWidth: '14rem' }}
                            autoFocus
                          />
                          <button
                            className="btn"
                            disabled={saving === l.job_no}
                            onClick={() => void savePosition(l.job_no, l.radius_meters)}
                          >
                            Save
                          </button>
                          <button
                            className="btn btn-secondary"
                            onClick={() => {
                              setMoving(null)
                              setCoords('')
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <>
                          {Number(l.latitude).toFixed(6)}, {Number(l.longitude).toFixed(6)}
                          <div>
                            <a
                              href={`https://www.google.com/maps?q=${l.latitude},${l.longitude}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Look at it
                            </a>
                          </div>
                        </>
                      )}
                    </td>
                    <td className="num">
                      <input
                        type="number"
                        step="25"
                        min="25"
                        max="20000"
                        style={{ width: '6rem' }}
                        value={radius[l.job_no] ?? String(l.radius_meters)}
                        onChange={(e) =>
                          setRadius((r) => ({ ...r, [l.job_no]: e.target.value }))
                        }
                        onBlur={() => void reradius(l)}
                        aria-label={`Radius for ${l.job_no}`}
                      />
                    </td>
                    <td className="num">{l.reading_count}</td>
                    <td>
                      {l.pinned_by ?? <span className="muted">–</span>}
                      <div className="muted" style={{ fontSize: '0.75rem' }}>
                        {l.geocode_source}
                      </div>
                    </td>
                    <td>
                      {saving === l.job_no ? (
                        <span className="muted">Saving…</span>
                      ) : (
                        moving !== l.job_no && (
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button
                              className="btn btn-secondary"
                              style={{ minHeight: 32 }}
                              onClick={() => {
                                setMoving(l.job_no)
                                setCoords(`${l.latitude}, ${l.longitude}`)
                              }}
                            >
                              Move
                            </button>
                            <button
                              className="btn btn-secondary"
                              style={{ minHeight: 32 }}
                              onClick={() => void clearPin(l)}
                            >
                              Clear
                            </button>
                          </div>
                        )
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
